import { autoinject } from "aurelia-dependency-injection";
import { useView } from "aurelia-framework";
import KeyDownEvent = JQuery.KeyDownEvent;
import { BindingEngine, computedFrom, observable } from "aurelia-binding";
import { CustomGridPageBase } from "../../../../../../common/custom-grid-page.base";
import { I18N } from "aurelia-i18n";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { RightShipDialogServiceConstants } from "../../../../../../../../constants/basics/rightship-dialog.constants";
import { DataGridColumn, DataType, ItemSource, SourceType, QueryFilter } from "../../../../../../../../models/queries/queries.models";
import { CustomerSubscriptionService } from "../../../../../../../../services/modules/customer-subscription-client.service";
import { CustomerSubscriptionModel, SubscriptionProductCodes } from "../../../../../../../../models/customers/customer-subscription.models";
import { MobileHelper } from "../../../../../../../../helpers/mobile-helper";
import * as moment from "moment";
import { SubscriptionHelper } from "../../../../helpers/subscription-helper";

@autoinject
@useView('views/pages/modules/my-subscription/tabs/information-services/tabs/my-companies/my-companies.html')
export class MyCompanies extends CustomGridPageBase {

    public itemSource: ItemSource = null;
    public customerSubscription: CustomerSubscriptionModel;
    public initialFilters: QueryFilter[] = [];

    @observable()
    filter: string;

    public customerId: number;
    private _reloadSubscription: () => void;
    public isRightShipUser: boolean;

    @computedFrom('customerId')
    get currentCustomerId() {
        if(this.customerId) {
            this.setInitialFilters();
            this.itemSource = this.getItemSource();
        }
        return this.customerId;
    }

    //Get subscription end date
    @computedFrom("customerSubscription")
    get subscriptionEndDate() {
        if(this.customerSubscription?.doesNotExpire) {
            return "Does not expire";
        }
        else if(this.customerSubscription?.subscriptionEndDate) {
            return moment(this.customerSubscription.subscriptionEndDate).format('DD MMM YYYY');
        }
        else {
            return "N/A";
        }
    }

    constructor(i18N: I18N, bindingEngine: BindingEngine,
        private dialogClientService: DialogClientService,
        private customerSubscriptionService: CustomerSubscriptionService, private subscriptionHelper: SubscriptionHelper) {
        super(i18N, 'Companies', bindingEngine);
        // this.itemSource = this.getItemSource();
    }

    @computedFrom("dataGridVM.unfilteredCount")
    get numOfCompanies() {
        if (this.dataGridVM && this.dataGridVM.unfilteredCount && this.dataGridVM.unfilteredCount > 0)
            return this.dataGridVM.unfilteredCount;
        return 0;
    }

    @computedFrom("customerSubscription", "customerSubscription.isExpired", "customerSubscription.metadata.currentCompanyChangesCount", "customerSubscription.metadata.maxCompanyChangesCount")
    public get canAddCompanies() {
        return this.customerSubscription && !this.customerSubscription.isExpired && this.customerSubscription.metadata.currentCompaniesCount < this.customerSubscription.metadata.maxCompaniesCount;
    }

    @computedFrom("customerSubscription", "customerSubscription.isExpired", "customerSubscription.metadata.currentCompanyChangesCount", "customerSubscription.metadata.maxCompanyChangesCount")
    public get canChangeCompanies() {
        return this.customerSubscription && !this.customerSubscription.isExpired && this.customerSubscription.metadata.currentCompanyChangesCount < this.customerSubscription.metadata.maxCompanyChangesCount;
    }

    activate(model: {customerId: number, subscription: CustomerSubscriptionModel, reloadSubscription: () => void, isRightShipUser: boolean}) {
        //this.getMySubscription();
        this.customerId = model.customerId;
        this.customerSubscription = model.subscription;
        this._reloadSubscription = model.reloadSubscription;
        this.isRightShipUser = model.isRightShipUser;
    }

    attached() {
        // this.getMySubscription();
    }

    getMySubscription(reload: boolean = false) {
        this.customerSubscriptionService.getMySubscription(SubscriptionProductCodes.informationServices, reload).then(response => {
            this.customerSubscription = response.customerSubscription;
        });
    }

    filterKeyDown($event) {
        const e: KeyDownEvent = $event.detail;

        if (e.key == "Enter") {
            this.setInitialFilters();
            this.itemSource = this.getItemSource();
        }
    }

    addCompany() {
        if (!this.canAddCompanies) {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.UpgradeSubscriptionDialog, { content: "To continue, you must upgrade your subscription as you have surpassed the maximum allowance for adding companies or making changes to existing ones." }, true);
        }
        else {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.AddCompanyToSubscriptionDialog, {
                onSaved: () => {
                    this.itemSource = this.getItemSource();
                    this._reloadSubscription();
                },
                customerId: this.customerId,
            }, true, false, false, "right");
        }
    }

    upgradeSubscription() {
        const url: string = this.subscriptionHelper.getUpgradeSubscriptionUrl();
        window.open(url, "_blank");
    }

    openChangeCompanyDialog(company: any) {
        if (this.customerSubscription?.metadata?.maxCompanyChangesCount <= this.customerSubscription?.metadata?.currentCompanyChangesCount) {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.UpgradeSubscriptionDialog, { content: "You need to upgrade your subscription to make company changes." }, true);
        } else {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.ChangeCompanySubscriptionDialog,
                {
                    onSaved: () => {
                        this.itemSource = this.getItemSource();
                        this._reloadSubscription();
                    },
                    companyId: company.id,
                    customerSubscription: this.customerSubscription,
                    customerId: this.customerId,
                }, true, false, false, "right");
        }
    }

    getItemSource() {
        this.columns = this.getColumns();
        const sourceUrl = this.isRightShipUser ? `odata/CustomerCompanies?customerId=${this.customerId}` : `odata/CustomerCompanies`;
        return {
            sourceType: SourceType.ODataUrl,
            source: sourceUrl,
            columns: this.columns,
            initialFilters: this.initialFilters,
            initialSorting: this.initialSorting
        };
    }

    getColumns(): DataGridColumn[] {
        return [
            {
                dataType: DataType.CustomTemplate,
                value: '<div class="row"><div class="column">' +
                    '<a class="link" href="/company/${item.companyId}">${item.name}</a>' +
                    '</div>' +
                    '<comp-icon class="icon-change margin-left-24" icon="arrow-reset" if.bind="canChangeCompanies()" click.trigger="changeCompany(item)"></comp-icon>' +
                    '</div>',
                name: "name",
                title: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? "" : "Name",
                instancePerRow: true,
                sortOrder: 1,
                columnOrder: 1,
                isHidden: false,
                isSortable: true,
                parameter: {
                    item: null,
                    changeCompany: (item) => { 
                        this.openChangeCompanyDialog(item);
                    },
                    canChangeCompanies: () => this.canChangeCompanies
                }
            },
            {
                dataType: DataType.Default,
                name: "country",
                title: "Country",
                sortOrder: 2,
                columnOrder: 2,
                isSortable: true,
                skipCollate: true
            },
            {
                dataType: DataType.Default,
                name: "registrationCountry",
                title: "Nationality of Registration",
                sortOrder: 3,
                columnOrder: 3,
                isSortable: true,
                skipCollate: true
            },
            {
                dataType: DataType.Default,
                name: "controlCountry",
                title: "Nationality of Control",
                sortOrder: 4,
                columnOrder: 4,
                isSortable: true,
                skipCollate: true
            },
            {
                dataType: DataType.Default,
                name: "docCount",
                title: "Vessels under Management",
                sortOrder: 5,
                columnOrder: 5,
                isSortable: true,
                skipCollate: true
            },
            {
                columnOrder: 6,
                sortOrder: 6,
                dataType: DataType.DateTime,
                name: 'addedDate',
                title: 'Action',
                isSortable: true,
                valueConverter: (date: Date | string) => date ? 'Added ' + moment(date).format('DD MMM YYYY'):'',
            }
        ];
    }

    private setInitialFilters() {
        const initialFilters = [];
        initialFilters.push(QueryFilter.getQuery("name", this.filter?.toLowerCase(), "contains(tolower({0}), '{1}')", DataType.Default, true, false, 'company-search-filters', true, true))
        this.initialFilters = initialFilters;
    }

    filterChanged() {
        if ((this.filter && this.filter.length >= 5) || this.filter == "") {
            this.setInitialFilters();
            this.itemSource = this.getItemSource();
        }
    }
}